import { createFileRoute } from '@tanstack/react-router'
import { DashboardLayout } from '@/components/layout'
import { atsNav } from '@/lib/atsNav'
import { currentTenant, currentUser, analytics } from '@/lib/mocks'
import { PageHeader, StatTile } from '@/lib/atsUi'
import { Card, CardContent } from '@/components/ui/card'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from 'recharts'

const PIE_COLOURS = ['#155DA8', '#5FA63C', '#E0512E', '#888780']

const totalPlacements = analytics.placementsTrend.reduce(
  (sum, d) => sum + d.placements,
  0,
)

const sourceTotal = analytics.sourceMix.reduce((sum, d) => sum + d.count, 0)

function HiringFunnelChart() {
  return (
    <div style={{ height: 260 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={analytics.funnel}
          layout="vertical"
          margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
        >
          <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <YAxis
            type="category"
            dataKey="stage"
            width={96}
            tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
          />
          <XAxis
            type="number"
            domain={[0, 240]}
            tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
            contentStyle={{
              fontSize: 12,
              borderRadius: 6,
              border: '1px solid hsl(var(--border))',
              background: 'hsl(var(--popover))',
              color: 'hsl(var(--popover-foreground))',
            }}
          />
          <Bar dataKey="count" fill="#155DA8" radius={[0, 4, 4, 0]} isAnimationActive={false}>
            <LabelList dataKey="count" position="right" style={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

function PlacementsTrendChart() {
  return (
    <div style={{ height: 260 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={analytics.placementsTrend}
          margin={{ top: 4, right: 16, bottom: 4, left: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            label={{ value: 'Placements', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: 'hsl(var(--muted-foreground))' } }}
          />
          <Tooltip
            contentStyle={{
              fontSize: 12,
              borderRadius: 6,
              border: '1px solid hsl(var(--border))',
              background: 'hsl(var(--popover))',
              color: 'hsl(var(--popover-foreground))',
            }}
          />
          <Area
            type="monotone"
            dataKey="placements"
            stroke="#155DA8"
            fill="#155DA8"
            fillOpacity={0.15}
            strokeWidth={2}
            dot={{ r: 3, fill: '#155DA8', strokeWidth: 0 }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

function SourceMixChart() {
  return (
    <>
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={analytics.sourceMix}
              dataKey="count"
              nameKey="source"
              cx="50%"
              cy="50%"
              outerRadius={80}
              innerRadius={40}
              paddingAngle={2}
              isAnimationActive={false}
            >
              {analytics.sourceMix.map((_, index) => (
                <Cell key={index} fill={PIE_COLOURS[index % PIE_COLOURS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                fontSize: 12,
                borderRadius: 6,
                border: '1px solid hsl(var(--border))',
                background: 'hsl(var(--popover))',
                color: 'hsl(var(--popover-foreground))',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
        {analytics.sourceMix.map((item, index) => (
          <div key={item.source} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span
              className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
              style={{ background: PIE_COLOURS[index % PIE_COLOURS.length] }}
            />
            {item.source} · {Math.round((item.count / sourceTotal) * 100)}%
          </div>
        ))}
      </div>
    </>
  )
}

function AnalyticsPage() {
  return (
    <DashboardLayout title="Analytics" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <PageHeader
        title="Analytics"
        subtitle="How your desk is performing — funnel, sources, and trend."
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatTile label="Time to fill" value={`${analytics.timeToFillDays} days`} />
        <StatTile label="Offer accept rate" value={`${analytics.offerAcceptRate}%`} />
        <StatTile label="Placements (6 mo)" value={String(totalPlacements)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="pt-5">
            <h2 className="text-sm font-medium text-muted-foreground">Hiring funnel</h2>
            <p className="mb-3 text-xs text-muted-foreground/80">Cumulative across all roles (last 6 months)</p>
            <HiringFunnelChart />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <h2 className="text-sm font-medium text-muted-foreground mb-3">Placements trend</h2>
            <PlacementsTrendChart />
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardContent className="pt-5">
            <h2 className="text-sm font-medium text-muted-foreground mb-3">Source mix</h2>
            <SourceMixChart />
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}

export const Route = createFileRoute('/analytics')({
  component: AnalyticsPage,
})
